import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TitleBar from "../../common/title-bar/title-bar";
import AdminActions from "../../common/admin-actions";
import DetailsContainer from "../../common/details-container";
import AccountsIcon from "../../icons/accounts-icon";
import DistributorIcon from "../../icons/distributor-icon";
import TableOptionsCell from "../../common/table/table-options-cell";
import Table from "../../common/table";
import StatusIndicator from "../../common/status-indicator";
import Divider from "../../common/divider/divider";
import Text from "../../common/typography/text";
import ModalView from "../../common/modal";
import InsuranceStatusSelector from "../../common/insurance-status-selector";
import DropdownComponent from "../../common/dropdown-input";
import TextInput from "../../common/text-input";
import TextAreaComponent from "../../common/text-area";
import FileUpload from "../UploadFileNewComponent/UploadFile";
import { Checkbox, Modal, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Button from "../../common/button/button";
import APIService from "../../Service/APIService";
import dayjs from "dayjs";
import QuickOrder from "./QuickOrder";
import TwoCheckIcon from "../../icons/two-check-icon";
import DashIcon from "../../icons/dash-icon";
import VerticalDashIcon from "../../icons/vertical-dash-icon";
import CalenderIcon from "../../icons/calender-icon";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { createIVRSubmissionPdfBlob } from "./IVRSubmissionPdf";
import { useAuth } from "../AuthProvider.js";
import FailureIcon from "../../icons/failure-icon";
import Swal from "sweetalert2";
import { hasPermission } from "../../Config/permissions.js";
import { canDeleteIvrSubmission } from "./ivrSubmissionAccess.js";


const IVRDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { patientID } = location.state || {};
  const { user } = useAuth();
  const [addProductModal, setAddProductModal] = useState(false);
  const [activeTab, setActiveTab] = useState({
    key: "pending",
    text: "Pending",
  });
  const [infoFields, setInfoFields] = useState([
    {
      FieldType: {
        label: "Text Field",
        value: "text-field",
      },
      FieldName: "",
    },
  ]);

  const [notifyUsersByTab, setNotifyUsersByTab] = useState({
    pending: false,
    submitted: false,
    covered: false,
    infoRequired: true,
    notCovered: false,
  });

  const [openQuickOrder, setOpenQuickOrder] = useState(false);
  const [selectedQuickOrderProduct, setSelectedQuickOrderProduct] = useState(null);

  const shouldNotifyUsers = Boolean(notifyUsersByTab?.[activeTab?.key]);

  const STATUS_DISPLAY_MAP = {
    pending: "Pending",
    covered: "Covered",
    submitted: "Submitted",
    infoRequired: "Info Required",
    notCovered: "notCovered",
  };

  const normalizeStatusKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim().toLowerCase();
    if (!raw) return "";
    const compact = raw.replace(/[^a-z]/g, "");
    switch (compact) {
      case "pending":
        return "pending";
      case "submitted":
        return "submitted";
      case "covered":
        return "covered";
      case "inforequired":
        return "infoRequired";
      case "notcovered":
        return "notCovered";
      default:
        return raw;
    }
  };

  const getStatusDisplay = (value) => {
    const key = normalizeStatusKey(value);
    return STATUS_DISPLAY_MAP[key] ?? value ?? "";
  };

  const getStatusForSave = (value) => {
    const key = normalizeStatusKey(value);
    return STATUS_DISPLAY_MAP[key] ?? value ?? "";
  };

  const isCoveredStatus = (status) =>
    String(status || "").toLowerCase() === "covered";

  const handleOpenQuickOrder = (rowData) => {
    if (!rowData || !isCoveredStatus(rowData?.InsuranceStatus)) {
      return;
    }
    setSelectedQuickOrderProduct(rowData);
    setOpenQuickOrder(true);
  };

  const handleCloseQuickOrder = () => {
    setOpenQuickOrder(false);
  };

  const ivrDetailsRowData = [
    {
      PatientID: `${patientID}`,
      ProductName: "AmchoPlast",
      ManufacturerName: "SimpliMax",
      CaseID: "JHJ2123",
      InsuranceStatusText: "Covered",
      InsuranceStatus: "covered",
      InsuranceStatusType: "success",
      AssociatedOrders: 2,
      ApplicationDate: "",
      ApplicationNumber: 1,
    },
    {
      PatientID: `${patientID}`,
      ProductName: "AmchoPlast",
      ManufacturerName: "SimpliMax",
      CaseID: "JHJ2123",
      InsuranceStatusText: "Not Covered",
      InsuranceStatus: "notCovered",
      InsuranceStatusType: "failure",
      AssociatedOrders: 5,
      ApplicationDate: "2026-01-12T01:02:32.000Z",
      ApplicationNumber: 3,
    },
    {
      PatientID: `${patientID}`,
      ProductName: "AmchoPlast",
      ManufacturerName: "SimpliMax",
      CaseID: "JHJ2123",
      InsuranceStatusText: "Pending",
      InsuranceStatus: "pending",
      InsuranceStatusType: "default",
      AssociatedOrders: 5,
      ApplicationDate: "",
      ApplicationNumber: 2,
    },
    {
      PatientID: `${patientID}`,
      ProductName: "AmchoPlast",
      ManufacturerName: "SimpliMax",
      CaseID: "JHJ2123",
      InsuranceStatusText: "Submitted",
      InsuranceStatus: "submitted",
      InsuranceStatusType: "submitted",
      AssociatedOrders: 5,
      ApplicationDate: "2025-08-20T01:02:32.000Z",
      ApplicationNumber: 6,
    },
    {
      PatientID: `${patientID}`,
      ProductName: "AmchoPlast",
      ManufacturerName: "SimpliMax",
      CaseID: "JHJ2123",
      InsuranceStatusText: "Info Required",
      InsuranceStatus: "infoRequired",
      InsuranceStatusType: "warning",
      AssociatedOrders: 5,
      ApplicationDate: "2025-08-26T01:02:32.000Z",
      ApplicationNumber: 4,
    },
  ];

  const ivrDetailsColDefs = [
    {
      headerName: "Product Name",
      field: "ProductGroupName",
      filter: true,
    },
    {
      headerName: "Manufacturer Name",
      field: "ManufacturerName",
      filter: true,
    },
    {
      headerName: "Case ID",
      field: "CaseID",
      filter: true,
    },
    {
      headerName: "Insurance Status",
      field: "InsuranceStatus",
      filter: true,
      cellRenderer: (params) => <StatusIndicator params={params} />,
    },
    {
      headerName: "Associated Orders",
      field: "AssociatedOrders",
      filter: true,
    },
    {
      headerName: "Options",
      field: "",
      cellRenderer: (params) => (
        <TableOptionsCell
          optionOne="View"
          optionOneType="primary"
          optionOneClickPath={{
            pathname: `/IVRProduct/${params.data.ID}`,
          }}
          type="IVRDetails"
          moreData={{ ivrProductData: params.data, PatientID: ivrData?.IVRASID }}
          optionTwo={isCoveredStatus(params?.data?.InsuranceStatus) ? "Quick Order" : null}
          optionTwoType="success"
          optionTwoClickFn={(rowData) => handleOpenQuickOrder(rowData)}
          rowData={params.data}
        />
      ),
    },
  ];

  const [ivrData, setIVRData] = useState(null);
  const [ivrAccount, setIvrAccount] = useState(null);
  const [ivrBillingAccount, setIvrBillingAccount] = useState(null);
  const [ivrProductGroups, setIVRProductGroups] = useState([]);
  const [ivrParentAccount, setIvrParentAccount] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSoftDeleteModal, setOpenSoftDeleteModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSoftDeleting, setIsSoftDeleting] = useState(false);
  const [isAddProductSubmitting, setIsAddProductSubmitting] = useState(false);
  const [
    isClearingUnavailableProductsMessage,
    setIsClearingUnavailableProductsMessage,
  ] = useState(false);
  const deleteInFlightRef = useRef(false);

  const getHomePath = (currentUser) => {
    const userType = currentUser?.Type;
    if (userType === "Account" && currentUser?.AccountID) {
      return `/Account/${currentUser.AccountID}`;
    }
    if (userType === "Distributor" && currentUser?.DistributorID) {
      return `/Distributor/${currentUser.DistributorID}`;
    }
    if (userType === "Manufacturer" && currentUser?.ManufacturerID) {
      return `/Manufacturer/${currentUser.ManufacturerID}`;
    }
    return "/";
  };

  const redirectToHome = () => {
    const path = getHomePath(user);
    if (!path) return;
    if (window.location.pathname !== path) {
      navigate(path, { replace: true });
    }
  };

  const getIVRProductsData = async () => {
    const serv = new APIService();
    try {
      const resp = await serv.getDataFromBackend("/ivr/getivrproducts?ivrId=" + id);
      const data = resp?.data?.data ?? resp?.data ?? {};
      console.log("IVR Products: ", data);
      const ivrWithWounds = data.ivr
        ? { ...data.ivr, WoundDetails: data.woundDetails || [] }
        : null;
      setIVRData(ivrWithWounds);
      const normalizedProducts = (data.ivrProducts || []).map((product) => ({
        ...product,
        InsuranceStatus: normalizeStatusKey(product?.InsuranceStatus),
      }));
      setIVRProductGroups(normalizedProducts);
      setIvrAccount(data.ivrAccount || null);
      setIvrParentAccount(data.ivrParentAccount || null);
      setIvrBillingAccount(data.ivrBillingAccount || null);
    } catch (error) {
      const status = error?.response?.status;
      if ([403, 404, 410].includes(status)) {
        redirectToHome();
        return;
      }
      console.error("Failed to load IVR details:", error);
    }
  }

  useEffect(() => {
    getIVRProductsData();
  }, []);

  const [productGroupOptions, setProductGroupOptions] = useState([]);

  const getAddProductFormOptions = async () => {
    const serv = new APIService();
    const resp = await serv.getDataFromBackend("/ivr/AddIVRProductFormOptions?ivrId=" + id);
    const data = resp?.data?.data ?? resp?.data ?? {};
    console.log("Add Product Form Options: ", data);
    setProductGroupOptions(data.productGroups || []);
  }

  useEffect(() => {
    if (addProductModal && productGroupOptions.length === 0) {
      getAddProductFormOptions();
    }
  }, [addProductModal]);

  const canDownloadDelete =
    user?.Type === "Admin" &&
    hasPermission(user, "ivr.download_phi") &&
    ivrData &&
    ivrData?.IsDeleted === false;
  const canSoftDelete = canDeleteIvrSubmission(user, ivrData);
  const canAddProduct =
    ["Admin", "ASD", "Authorizer", "Viewer"].includes(user?.Type) &&
    hasPermission(user, "ivr_product.add");
  const canShowIvrActions =
    canDownloadDelete || canSoftDelete || canAddProduct;

  const buildDeletedByName = () => {
    const fullName = `${user?.FirstName ?? ""} ${user?.LastName ?? ""}`.trim();
    if (fullName) return fullName;
    return user?.Email || user?.Username || "Unknown User";
  };

  const sanitizeFilename = (value) => {
    if (!value) return "";
    return String(value)
      .replace(/[^a-zA-Z0-9._-]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 120);
  };

  const buildSubmissionPdfFiles = async () => {
    const products = Array.isArray(ivrProductGroups) ? ivrProductGroups : [];
    const productList = products.length > 0 ? products : [null];
    const baseName = `IVR_${ivrData?.IVRASID || ivrData?.ID || "Submission"}`;
    const pdfFiles = [];
    for (let index = 0; index < productList.length; index += 1) {
      const product = productList[index];
      const rawName =
        product?.ProductGroupName ||
        product?.ProductName ||
        product?.ProductGroup ||
        product?.Name ||
        (ivrData?.UnavailableProductsRequested) || "No products available for this Account. Please check the IVR on Tissue Vault for details.";
      const safeName = sanitizeFilename(rawName) || `Product_${index + 1}`;
      const pdfBlob = await createIVRSubmissionPdfBlob(ivrData, {
        ivrProductName: rawName,
        ivrProductStatus: product?.InsuranceStatus,
      });
      if (!pdfBlob) continue;
      const fileName = `${baseName}_${safeName}_${index + 1}.pdf`;
      pdfFiles.push({ data: pdfBlob, name: fileName });
    }
    return pdfFiles;
  };

  const downloadAll = async () => {
    if (!ivrData) {
      message.error("IVR data is not available yet.");
      return false;
    }
    if (isDownloading) return false;

    setIsDownloading(true);
    try {
      const apiServe = new APIService();
      let zip = new JSZip();
      let secureFilesFound = false;

      try {
        const secureZipResponse = await apiServe.downloadIvrSecureFilesZip(ivrData.ID);
        if (secureZipResponse?.data) {
          zip = await JSZip.loadAsync(secureZipResponse.data);
          secureFilesFound = true;
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status !== 404) {
          throw error;
        }
      }

      const pdfFiles = await buildSubmissionPdfFiles();
      pdfFiles.forEach((file) => {
        zip.file(file.name, file.data);
      });

      const hasFiles = secureFilesFound || pdfFiles.length > 0;
      if (!hasFiles) {
        message.warning("No files available to download.");
        return false;
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const zipName = `IVR_${ivrData?.IVRASID || ivrData?.ID || "files"}.zip`;
      saveAs(blob, zipName);
      return true;
    } catch (error) {
      console.error("Failed to download IVR files:", error);
      message.error("Failed to download IVR files.");
      return false;
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenDeleteModal = () => {
    if (deleteInFlightRef.current || isDownloading || isDeleting) {
      return;
    }
    if (!ivrData) {
      message.error("IVR data is not available yet.");
      return;
    }
    if (ivrData?.IsDeleted) {
      message.warning("This IVR form submission has already been deleted.");
      return;
    }
    if (user?.Type !== "Admin") {
      message.error("You do not have permission to delete this IVR.");
      return;
    }
    if (!canDownloadDelete) return;
    setOpenDeleteModal(true);
  };

  const handleCancelDelete = () => {
    if (deleteInFlightRef.current || isDownloading || isDeleting) return;
    setOpenDeleteModal(false);
  };

  const handleOkDelete = async () => {
    if (!ivrData?.ID || deleteInFlightRef.current || isDeleting || isDownloading) return;

    deleteInFlightRef.current = true;
    setIsDeleting(true);
    try {
      const didDownload = await downloadAll();
      if (!didDownload) {
        return;
      }

      const postObj = {
        ID: ivrData.ID,
        DeletedBy: buildDeletedByName(),
        DeletedByUserId: user?.UserID ?? user?.ID ?? null,
      };
      const apiServer = new APIService();
      const response = await apiServer.updateDataToBackend(
        postObj,
        "updateivr?type=Delete"
      );
      if (response?.status === 200 || response?.status === 201) {
        message.success("IVR form submission deleted successfully.");
        window.location.reload();
        return;
      } else {
        message.error("Failed to delete IVR submission.");
      }
    } catch (error) {
      console.error("Delete IVR submission failed:", error);
      message.error(
        error?.response?.data?.message ||
        "Failed to delete IVR submission. The IVR was kept unchanged."
      );
    } finally {
      deleteInFlightRef.current = false;
      setIsDeleting(false);
      setOpenDeleteModal(false);
    }
  };

  const handleOpenSoftDeleteModal = () => {
    if (isSoftDeleting) return;
    if (!ivrData) {
      message.error("IVR data is not available yet.");
      return;
    }
    if (!canSoftDelete) {
      message.error("You do not have permission to delete this IVR.");
      return;
    }
    setOpenSoftDeleteModal(true);
  };

  const handleCancelSoftDelete = () => {
    if (isSoftDeleting) return;
    setOpenSoftDeleteModal(false);
  };

  const handleSoftDelete = async () => {
    if (!ivrData?.ID || isSoftDeleting || !canSoftDelete) return;

    setIsSoftDeleting(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.addDataToBackend(
        { ivrId: ivrData.ID },
        "/ivr/deleteivr"
      );

      if (response?.status === 200 || response?.status === 201) {
        message.success("IVR deleted successfully.");
        navigate("/IVRDashboard", { replace: true });
        return;
      }

      message.error("Failed to delete IVR. Please try again.");
    } catch (error) {
      console.error("Soft delete IVR failed:", error);
      message.error(
        error?.response?.data?.message || "Failed to delete IVR. Please try again."
      );
    } finally {
      setIsSoftDeleting(false);
      setOpenSoftDeleteModal(false);
    }
  };

  const INSURANCE_ICON_CONFIG = {
    covered: {
      className: "success-circle-container",
      icon: <TwoCheckIcon className="icon-container-white" />,
    },
    notCovered: {
      className: "failure-circle-container",
      icon: <DashIcon className="icon-container-white" />,
    },
    infoRequired: {
      className: "warning-circle-container",
      icon: <VerticalDashIcon className="icon-container-white" />,
    },
  };

  const renderQuickOrderStatusIcon = useMemo(() => {
    const status = normalizeStatusKey(selectedQuickOrderProduct?.InsuranceStatus);
    if (!status) return null;
    const config = INSURANCE_ICON_CONFIG[status];
    if (!config) return null;
    return <div className={config.className}>{config.icon}</div>;
  }, [selectedQuickOrderProduct]);

  const QUICK_ORDER_MODAL_FIELDS = useMemo(() => {
    if (!selectedQuickOrderProduct) return [];
    return [
      {
        fieldTitle: "Patient ID",
        fieldValue: ivrData?.IVRASID,
        fieldColor: "successColorDark",
      },
      {
        fieldTitle: "CaseID",
        fieldValue: selectedQuickOrderProduct?.CaseID,
        fieldColor: "successColorDark",
      },
      {
        fieldTitle: "Product",
        fieldValue: selectedQuickOrderProduct?.ProductGroupName,
        fieldColor: "successColorDark",
      },
    ];
  }, [ivrData?.IVRASID, selectedQuickOrderProduct]);

  const renderQuickOrderModalFields =
    QUICK_ORDER_MODAL_FIELDS.map(({ fieldTitle, fieldValue, fieldColor }) => (
      <div className="quick-order-modal-field-component" key={fieldTitle}>
        <Text variant="bodyText">{fieldTitle}</Text>
        <Text variant="h1" color={fieldColor}>
          {fieldValue}
        </Text>
      </div>
    )) || null;

  const quickOrderRightHeadingText = "Application Date";
  const quickOrderRightHeadingValue = useMemo(() => {
    const rawDate =
      selectedQuickOrderProduct?.ApplicationDate ?? ivrData?.SubmissionDate;
    return rawDate ? dayjs(rawDate).format("MM-DD-YYYY") : "N/A";
  }, [ivrData?.SubmissionDate, selectedQuickOrderProduct]);

  const RENDER_INSURANCE_TABS = [
    {
      key: "pending",
      text: "Pending",
      onClick: () => setActiveTab({ key: "pending", text: "Pending" }),
    },
    {
      key: "submitted",
      text: "Submitted",
      onClick: () => setActiveTab({ key: "submitted", text: "Submitted" }),
    },
    {
      key: "covered",
      text: "Covered",
      onClick: () => setActiveTab({ key: "covered", text: "Covered" }),
    },
    {
      key: "infoRequired",
      text: "Info Required",
      onClick: () =>
        setActiveTab({ key: "infoRequired", text: "Info Required" }),
    },
    {
      key: "notCovered",
      text: "Not Covered",
      onClick: () => setActiveTab({ key: "notCovered", text: "Not Covered" }),
    },
  ];

  const addField = () => {
    setInfoFields((prev) => [
      ...prev,
      {
        FieldType: {
          label: "Text Field",
          value: "text-field",
        },
        FieldName: "",
      },
    ]);
  };

  const handleChangeEdit = (index, field, value) => {
    setInfoFields((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [caseID, setCaseID] = useState("");
  const [addProductNote, setAddProductNote] = useState("");
  const [addProductReportFiles, setAddProductReportFiles] = useState([]);
  const [addProductNoPhiCheck, setAddProductNoPhiCheck] = useState(false);
  const hasAddProductReportFile =
    Array.isArray(addProductReportFiles) && addProductReportFiles.length > 0;
  const isAddCoveredStatus = normalizeStatusKey(activeTab?.key) === "covered";
  const isAddProductDisabled =
    (isAddCoveredStatus && !hasAddProductReportFile) ||
    (hasAddProductReportFile && !addProductNoPhiCheck);

  useEffect(() => {
    if (addProductModal) return;
    setSelectedProductGroup(null);
    setCaseID("");
    setAddProductNote("");
    setAddProductReportFiles([]);
    setAddProductNoPhiCheck(false);
    setActiveTab({ key: "pending", text: "Pending" });
    setInfoFields([
      {
        FieldType: {
          label: "Text Field",
          value: "text-field",
        },
        FieldName: "",
      },
    ]);
  }, [addProductModal]);

  const handleAddProductReportFilesChange = (files) => {
    const nextFiles = Array.isArray(files) ? files : [];
    setAddProductReportFiles(nextFiles);
    setAddProductNoPhiCheck(false);
  };

  const resetAddProductModalState = () => {
    setSelectedProductGroup(null);
    setCaseID("");
    setAddProductNote("");
    setAddProductReportFiles([]);
    setInfoFields([
      {
        FieldType: {
          label: "Text Field",
          value: "text-field",
        },
        FieldName: "",
      },
    ]);
    setActiveTab({
      key: "pending",
      text: "Pending",
    });
  };

  const handleCloseAddProductModal = () => {
    if (isAddProductSubmitting) return;
    setAddProductModal(false);
    resetAddProductModalState();
  };

  const addProductGroup = async () => {
    if (isAddCoveredStatus && !hasAddProductReportFile) {
      message.error("A report file is required when status is Covered.");
      return;
    }
    if (hasAddProductReportFile && !addProductNoPhiCheck) {
      message.error("PHI certification is required when a report file is uploaded.");
      return;
    }
    if (isAddProductSubmitting) return;

    // API call to add product group to IVR
    const serv = new APIService();
    setIsAddProductSubmitting(true);
    const postObj = {
      ivrId: id,
      productGroupId: selectedProductGroup?.value,
      productGroupName: selectedProductGroup?.label,
      caseId: caseID,
      note: addProductNote,
      // reportFiles: addProductReportFiles,
      status: getStatusForSave(activeTab?.key),
      notifyUsers: shouldNotifyUsers,
      noPhiCheck: hasAddProductReportFile ? addProductNoPhiCheck : false,
    }
    const formData = new FormData();

    if (Array.isArray(addProductReportFiles)) {
      addProductReportFiles.forEach((file) => {
        formData.append("reportFiles", file);
      });
    }
    formData.append("data", JSON.stringify(postObj));

    try {
      const response = await serv.addFileDataToBackend(
        formData,
        "/ivr/addivrproduct",
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Add Product Group Response: ", response);
      if (response?.status === 200 || response?.status === 201) {
        setAddProductModal(false);
        resetAddProductModalState();
        await getIVRProductsData();
        message.success("Product added and status updated successfully.");
        return;
      }
      message.error("Failed to add product. Please try again.");
    } catch (error) {
      console.error("Add IVR product failed:", error);
      message.error(
        error?.response?.data?.message || "Failed to add product. Please try again."
      );
    } finally {
      setIsAddProductSubmitting(false);
    }
  }

  const canClearUnavailableProductsMessage =
    user?.Type === "Admin" &&
    !!ivrData?.UnavailableProductsRequested &&
    Array.isArray(ivrProductGroups) &&
    ivrProductGroups.length >= 0;

  const handleClearMessage = async () => {
    if (isClearingUnavailableProductsMessage) return;

    const result = await Swal.fire({
      title: "Clear Unavailable Product Message",
      text: "Are you sure you want to clear the unavailable product message?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    });

    if (!result?.isConfirmed) return;

    try {
      setIsClearingUnavailableProductsMessage(true);

      const serv = new APIService();
      const response = await serv.addDataToBackend(
        { ivrId: id },
        "/ivr/clearunavailableproductsmessage"
      )

      if (response?.status === 200 || response?.status === 201) {
        message.success("Message cleared successfully!");
        await getIVRProductsData();
        return;
      }
      message.error("Failed to clear message. Please try again.");

    } catch (error) {
      console.error("Failed to clear unavailable products message:", error);
      message.error(error?.response?.data?.message || "Failed to clear message. Please try again.");
    } finally {
      setIsClearingUnavailableProductsMessage(false);
    }

  }

  return (
    <>
      <div className="global-parent-container-wrapper">
        <TitleBar
          entityName={id ? `IVR ID ${id}` : undefined}
          label={patientID ? `Patient ID ${patientID}` : undefined}
          // invoiceNumber={patientID}
        />
        {/* download and delete condition to be added */}
        {canShowIvrActions &&
          <AdminActions
            secondButtonText={canDownloadDelete && !ivrData?.IsDeleted ? "Download + Delete" : undefined}
            secondButtonType={canDownloadDelete && !ivrData?.IsDeleted ? "failure" : undefined}
            secondButtonFn={canDownloadDelete && !ivrData?.IsDeleted ? handleOpenDeleteModal : undefined}
            thirdButtonText={canAddProduct ? "Add Product" : undefined}
            thirdButtonType={canAddProduct ? "success" : undefined}
            thirdButtonFn={canAddProduct ? () => setAddProductModal(true) : undefined}
            fourthButtonText="Delete"
            fourthButtonType="failure"
            fourthButtonFn={handleOpenSoftDeleteModal}
            fourthButtonCondition={canSoftDelete}
            ivrComponent={
              <>
                {ivrData?.IsDeleted &&
                  <>
                    <Divider type="vertical" />
                    <div className="ivr-actions-activity-box-container">
                      <Text variant="smallText">IVR details were downloaded</Text>
                      <div className="ivr-actions-activity-timestamp-container">
                        <Text variant="tinyText" color="lightText">
                          {dayjs(ivrData?.DeletedOn).format("MM-DD-YYYY HH:mm") || "N/A"}
                        </Text>
                        <Text variant="tinyText" color="lightText">
                          {/* By UserName */}
                          {ivrData?.DeletedBy || "N/A"}
                        </Text>
                      </div>
                    </div>
                  </>
                }
              </>
            }
            isSuperAdmin={canAddProduct}
            IVRPath={true}
          />
        }
        <DetailsContainer
          firstHeading="Ship To Account" // this is the IVR Account Name
          // accountName="Foot & Ankle Specialists of Central PA"
          accountName={ivrAccount?.AccountName || "N/A"}
          // isChild={true} // is IVR Account a child account
          isChild={ivrAccount?.IsChildAccount} // is IVR Account a child account
          // childAccount="Child Account of Foot & Ankle Specialists" // this is the IVR Parent Account Name if the IVR Account is a child account
          childAccount={ivrParentAccount ? `Child Account of ${ivrParentAccount.AccountName}` : "N/A"} // this is the IVR Parent Account Name if the IVR Account is a child account
          secondHeading="Bill To Account" // this is BillingAccount of IVR Account
          secondIcon={<AccountsIcon className="icon-container" />}
          // distributorName="Foot & Ankle Specialists of Central PA" // this is actually billing account
          distributorName={ivrAccount?.BillingAccountName || "N/A"} // this is actually billing account
          thirdHeading="Distributor" // this is IVR Distributor
          thirdIcon={<DistributorIcon className="icon-container" />}
          // manufacturerName="House" // this is Dist Name
          manufacturerName={ivrData?.DistributorName} // this is Dist Name
          rightFirstHeading="Submitted On"
          // invoiceDate="January 1, 2024" // this is IVR Submission Date
          invoiceDate={dayjs(ivrData?.SubmissionDate).format("MM-DD-YYYY")} // this is IVR Submission Date
          rightSecondHeading="Last Updated On"
          // invoiceDue="February 1, 2024" // this is IVR Last Updated Date
          invoiceDue={dayjs(ivrData?.updateAt).format("MM-DD-YYYY")} // this is IVR Last Updated Date
          rightThirdHeading="Submitted By"
          // invoiceDue="February 1, 2024" // this is IVR Last Updated Date
          submittedBy={ivrData?.SubmittedBy || "Unknown"} // this is IVR Last Updated Date

          accountID={ivrAccount?.ID}
          distributorID={ivrBillingAccount?.DistributorID}
          manufacturerID={ivrBillingAccount?.DistributorID}
          accountPath={"account"}
          distributorPath={"distributor"}

          showInsurance={true}
          // TODO(BE): BE does not currently return Primary/Secondary Insurance on the IVR payload.
          // Wire these to the real fields once the backend adds them (e.g. ivrData?.PrimaryInsurance / ivrData?.SecondaryInsurance).
          primaryInsurance={ivrData?.PrimaryInsurance}
          secondaryInsurance={ivrData?.SecondaryInsurance}
        />
        {ivrData?.UnavailableProductsRequested &&
          <div className="modal-warning-container-ivr">
            <FailureIcon
              className="warning-icon-container"
              width={16}
              height={16}
            />
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "16px", alignItems: "flex-start" }}>
              <Text>
                This account does not have approved products in their catalog, and has requested IVR for the following products:
                <br />{ivrData?.UnavailableProductsRequested}
              </Text>

              {canClearUnavailableProductsMessage &&
                <Button
                  type="text-link"
                  onClick={handleClearMessage}
                  disabled={isClearingUnavailableProductsMessage}
                >
                  {isClearingUnavailableProductsMessage ? "Clearing..." : "Clear Message"}
                </Button>
              }

            </div>
          </div>
        }
        <Table columnDefs={ivrDetailsColDefs} rowData={ivrProductGroups} />
        <Modal
          title="Delete IVR submission?"
          open={openSoftDeleteModal}
          onCancel={handleCancelSoftDelete}
          closable={!isSoftDeleting}
          maskClosable={!isSoftDeleting}
          keyboard={!isSoftDeleting}
          centered={true}
          footer={false}
        >
          
          <p>Are you sure? You won't be able to access this IVR submission after deletion.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button
              variant="ghost"
              onClick={handleCancelSoftDelete}
              disabled={isSoftDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="table"
              subVariant="failure"
              onClick={handleSoftDelete}
              disabled={isSoftDeleting}
            >
              {isSoftDeleting ? (
                <>
                  <LoadingOutlined spin style={{ marginRight: 8 }} />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </Modal>
        <Modal
          title="Are you sure you want to delete form submission?"
          open={openDeleteModal}
          onOk={handleOkDelete}
          onCancel={handleCancelDelete}
          closable={!isDeleting && !isDownloading}
          maskClosable={!isDeleting && !isDownloading}
          keyboard={!isDeleting && !isDownloading}
          centered={true}
          footer={false}
        >
          <p>
            This action will delete all the data from this IVR form submission,
            except for the following:
          </p>
          <ul>
            <li style={{ listStyleType: "disc" }}>IVRAS ID</li>
            <li style={{ listStyleType: "disc" }}>Form Submission Date</li>
            <li style={{ listStyleType: "disc" }}>Account Name</li>
            <li style={{ listStyleType: "disc" }}>Place of Service</li>
            <li style={{ listStyleType: "disc" }}>City</li>
            <li style={{ listStyleType: "disc" }}>State</li>
            <li style={{ listStyleType: "disc" }}>Zip</li>
            <li style={{ listStyleType: "disc" }}>
              Medicare Admin Contractor
            </li>
            <li style={{ listStyleType: "disc" }}>Patient DOB</li>
            <li style={{ listStyleType: "disc" }}>
              All fields pertaining to Primary Insurance
            </li>
            <li style={{ listStyleType: "disc" }}>
              All fields pertaining to Secondary Insurance
            </li>
            <li style={{ listStyleType: "disc" }}>Wound Type</li>
            <li style={{ listStyleType: "disc" }}>Wound Size</li>
            <li style={{ listStyleType: "disc" }}>Application CPT</li>
            <li style={{ listStyleType: "disc" }}>ICD-10 Diagnosis Code</li>
            <li style={{ listStyleType: "disc" }}>SNF</li>
            <li style={{ listStyleType: "disc" }}>Surgical Global Period</li>
            <li style={{ listStyleType: "disc" }}>CPT Codes</li>
          </ul>
          <p>
            You will no longer be able to download the PDF. This action cannot
            be undone.
          </p>
          <Button
            type="primary"
            onClick={handleOkDelete}
            style={{ background: "#ff0000" }}
            disabled={isDeleting || isDownloading}
          >
            {isDownloading ? (
              <>
                <LoadingOutlined spin style={{ marginRight: 8 }} />
                Downloading...
              </>
            ) : isDeleting ? (
              <>
                <LoadingOutlined spin style={{ marginRight: 8 }} />
                Deleting...
              </>
            ) : (
              "Download + Delete"
            )}
          </Button>
        </Modal>
      </div>
      {addProductModal && (
        <ModalView
          isOpen={addProductModal}
          onClose={handleCloseAddProductModal}
          modalTitle="Add Product and Update Status"
          textInputComponent={
            <div className="ivr-16-gap-modal-container">
              <InsuranceStatusSelector
                activeTab={activeTab}
                buttons={RENDER_INSURANCE_TABS}
              />
              <div className="field-group-wrapper">
                <div className="field-group" style={{ alignItems: "center" }}>
                  <DropdownComponent
                    label="Product Name"
                    placeholder="Select Product"
                    isRequired
                    options={productGroupOptions}
                    setValue={setSelectedProductGroup}
                    value={selectedProductGroup}
                  />
                  <TextInput
                    titleText="Case ID"
                    placeholder="Enter Case ID"
                    //   isDisabled
                    value={caseID}
                    setValue={setCaseID}
                  />
                </div>
              </div>
              <TextAreaComponent
                placeholder="Add a note"
                value={addProductNote}
                setValue={setAddProductNote}
              />
              {activeTab?.key !== "infoRequired" ? (
                <FileUpload
                  label="Upload File"
                  uploadedFiles={addProductReportFiles}
                  setUploadedFiles={handleAddProductReportFilesChange}
                />
              ) : null}
              {isAddCoveredStatus && !hasAddProductReportFile ? (
                <Text variant="tinyText" color="failureColor">
                  A report file is required when status is Covered.
                </Text>
              ) : null}
              {hasAddProductReportFile ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <Checkbox
                    checked={addProductNoPhiCheck}
                    onChange={(event) => {
                      setAddProductNoPhiCheck(event?.target?.checked === true);
                    }}
                  />
                  <Text variant="smallText">
                    I certify that this file contains no PHI (Protected Health
                    Information) about the patient{" "}
                    <span style={{ color: "red" }}>*</span>
                    <br />
                    <p className="input-subtitle">
                      {`File will be uploaded by: ${user?.FirstName || ""} ${user?.LastName || ""}`.trim()}
                    </p>
                  </Text>
                </div>
              ) : null}
              {activeTab?.key === "infoRequired" ? (
                <Button variant="text-link" onClick={() => addField()}>
                  Add User Input Field
                </Button>
              ) : null}
              {activeTab?.key === "infoRequired"
                ? infoFields?.map((field, index) => {
                  return (
                    <div className="field-group-wrapper" key={index}>
                      <div
                        className="field-group"
                        style={{ alignItems: "center" }}
                      >
                        <DropdownComponent
                          label="Field Type"
                          placeholder=""
                          isRequired
                          value={field.FieldType}
                          // setValue={setFieldTypeValue}
                          onChange={(option) =>
                            handleChangeEdit(index, "FieldType", option)
                          }
                          options={[
                            { label: "Text Field", value: "text-field" },
                            {
                              label: "Upload Field",
                              value: "upload-field",
                            },
                          ]}
                        />
                        <TextInput
                          titleText={
                            field.FieldType?.value === "upload-field"
                              ? "Upload File Name"
                              : "File Name"
                          }
                          placeholder={
                            field.FieldType?.value === "upload-field"
                              ? "Enter file name to be uploaded"
                              : "Enter field name"
                          }
                          value={field?.FieldName}
                          onChange={(e) =>
                            handleChangeEdit(
                              index,
                              "FieldName",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })
                : null}
              <div style={{ display: "flex", gap: "8px" }}>
                <Checkbox
                  checked={shouldNotifyUsers}
                  onChange={(event) => {
                    setNotifyUsersByTab((prev) => ({
                      ...prev,
                      [activeTab?.key]: event?.target?.checked === true,
                    }));
                  }}
                />
                <Text variant="smallText">
                  Notify associated users via email
                </Text>
              </div>
            </div>
          }
          footerButtonWrapper={
            <div
              className="footer-button-wrapper-flex-end"
              style={{ gap: "12px" }}
            >
              <Button
                variant="table"
                subVariant=""
                onClick={handleCloseAddProductModal}
                disabled={isAddProductSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="table"
                subVariant="primary"
                onClick={addProductGroup}
                disabled={isAddProductSubmitting}
              >
                {isAddProductSubmitting ? (
                  <>
                    <LoadingOutlined spin style={{ marginRight: 8 }} />
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          }
        />
      )}
      {openQuickOrder && selectedQuickOrderProduct && (
        <QuickOrder
          isOpen={openQuickOrder}
          onClose={handleCloseQuickOrder}
          ivrData={ivrData}
          ivrProductData={selectedQuickOrderProduct}
          ivrAccount={ivrAccount}
          ivrBillingAccount={ivrBillingAccount}
          ivrParentAccount={ivrParentAccount}
          insuranceStatusPanelProps={{
            insuranceStatus: normalizeStatusKey(
              selectedQuickOrderProduct?.InsuranceStatus || ""
            ),
            insuranceStatusText:
              selectedQuickOrderProduct?.InsuranceStatusText ??
              getStatusDisplay(selectedQuickOrderProduct?.InsuranceStatus),
            leftHeadingIcon: renderQuickOrderStatusIcon,
            // rightHeadingIcon: <CalenderIcon className="icon-container" />,
            // rightHeadingText: quickOrderRightHeadingText,
            // rightHeadingValue: quickOrderRightHeadingValue,
            modalVariantComponent: renderQuickOrderModalFields,
          }}
        />
      )}
    </>
  );
};

export default IVRDetails;
